/* eslint-disable react/prop-types */
/**
 * SATE — Standard Advisory Template Section
 * =========================================
 * Layer 2 of the communication stack. Renders inside the pricing card
 * BETWEEN the deterministic explanation and the Contextual Customer
 * Note. FOH can accept the suggested workflow template, edit any of
 * its text, toggle individual outcome blocks on/off, or suppress the
 * whole template if it's not appropriate for this job.
 *
 * Auto-attach behaviour:
 *   • If the JG has no saved SATE AND the rule engine matches a
 *     template AND the JG isn't explicitly suppressed → the suggested
 *     template is attached with all outcomes enabled by default.
 *   • If FOH has edited or suppressed, those persist forever (foh_edited
 *     flag protects against future rule-engine updates clobbering work).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ChevronDown,
  ChevronRight,
  Workflow,
  RotateCcw,
  Eye,
  EyeOff,
} from "lucide-react";

export default function SATESection({
  yestimateId,
  jobGroup,
  jobIdx,
  onChange,
}) {
  const saved = jobGroup.sate || null;
  const [sate, setSate] = useState(saved);
  const [defaultSate, setDefaultSate] = useState(null);
  const [expanded, setExpanded] = useState(!!saved && !saved.suppressed);
  const [loading, setLoading] = useState(false);

  // Initial fetch + auto-attach on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/sate/suggest?yestimate_id=${yestimateId}&jg_id=${jobGroup.id}`,
        );
        if (cancelled) return;
        const d = r.data || {};
        setDefaultSate(d.default_sate || null);
        // Auto-attach when there's a match and FOH hasn't already
        // touched (saved/suppressed) the SATE for this JG.
        if (!d.has_saved && d.default_sate) {
          setSate(d.default_sate);
          onChange?.(d.default_sate);
          setExpanded(true);
        }
      } catch { /* silent — keeps FOH UX clean if matcher is down */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobGroup.id]);

  // Nothing to show — no saved SATE and no matching template
  if (!loading && !sate && !defaultSate) return null;

  const push = (next) => {
    setSate(next);
    onChange?.(next);
  };

  const toggleIntro = (val) => {
    push({ ...sate, intro_enabled: val });
  };

  const updateIntro = (text) => {
    push({ ...sate, intro: text, foh_edited: true });
  };

  const toggleOutcome = (key, val) => {
    push({
      ...sate,
      outcomes: (sate.outcomes || []).map((o) =>
        o.key === key ? { ...o, enabled: val } : o,
      ),
    });
  };

  const updateOutcomeText = (key, text) => {
    push({
      ...sate,
      outcomes: (sate.outcomes || []).map((o) =>
        o.key === key ? { ...o, text } : o,
      ),
      foh_edited: true,
    });
  };

  const handleSuppress = () => {
    push({ ...(sate || defaultSate), suppressed: true });
  };

  const handleRestore = () => {
    push({ ...sate, suppressed: false });
    setExpanded(true);
  };

  const handleResetToDefault = () => {
    if (!defaultSate) return;
    push({ ...defaultSate, suppressed: false });
  };

  // Suppressed → tiny restore strip
  if (sate?.suppressed) {
    return (
      <Card
        className="mt-3 border-slate-200 bg-slate-50/60 px-3 py-2 flex items-center justify-between"
        data-testid={`sate-section-suppressed-${jobIdx}`}
      >
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <EyeOff className="w-3.5 h-3.5" />
          Recommended Approach hidden from customer
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleRestore}
          className="h-7 text-xs"
          data-testid={`sate-restore-${jobIdx}`}
        >
          <Eye className="w-3.5 h-3.5 mr-1" /> Restore
        </Button>
      </Card>
    );
  }

  if (!sate) return null;
  const enabledOutcomesCount = (sate.outcomes || []).filter((o) => o.enabled).length;

  return (
    <Card
      className="mt-3 border-slate-200 bg-blue-50/40"
      data-testid={`sate-section-${jobIdx}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-blue-100/50 transition-colors rounded-t-lg"
        data-testid={`sate-toggle-${jobIdx}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Workflow className="w-4 h-4 text-blue-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2 flex-wrap">
              Recommended Approach
              <span className="text-[10px] font-medium normal-case text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                {sate.label}
              </span>
              {sate.foh_edited && (
                <span className="text-[10px] font-medium normal-case text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded">
                  Edited
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 italic">
              How this {sate.label.toLowerCase()} typically progresses ·
              {" "}{enabledOutcomesCount} outcome{enabledOutcomesCount === 1 ? "" : "s"} on
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3" data-testid={`sate-body-${jobIdx}`}>
          {/* Intro */}
          <div className="bg-white border border-slate-200 rounded p-2">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
                Introduction
              </Label>
              <Switch
                checked={!!sate.intro_enabled}
                onCheckedChange={toggleIntro}
                data-testid={`sate-intro-enabled-${jobIdx}`}
              />
            </div>
            <Textarea
              value={sate.intro || ""}
              onChange={(e) => updateIntro(e.target.value)}
              className="text-xs min-h-[60px]"
              disabled={!sate.intro_enabled}
              data-testid={`sate-intro-text-${jobIdx}`}
            />
          </div>

          {/* Outcome blocks */}
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-600 mb-1.5 block">
              Possible Outcomes
            </Label>
            <div className="space-y-2">
              {(sate.outcomes || []).map((o) => (
                <div key={o.key} className="bg-white border border-slate-200 rounded p-2">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-[11px] font-semibold text-slate-600 capitalize">
                      {o.key.replace(/_/g, " ")}
                    </span>
                    <Switch
                      checked={!!o.enabled}
                      onCheckedChange={(v) => toggleOutcome(o.key, v)}
                      data-testid={`sate-outcome-enabled-${jobIdx}-${o.key}`}
                    />
                  </div>
                  <Textarea
                    value={o.text || ""}
                    onChange={(e) => updateOutcomeText(o.key, e.target.value)}
                    className="text-xs min-h-[44px]"
                    disabled={!o.enabled}
                    data-testid={`sate-outcome-text-${jobIdx}-${o.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer controls */}
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2">
              {sate.foh_edited && defaultSate && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleResetToDefault}
                  className="h-7 text-xs text-slate-500"
                  data-testid={`sate-reset-${jobIdx}`}
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset to default
                </Button>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSuppress}
              className="h-7 text-xs text-slate-500 hover:text-red-700"
              data-testid={`sate-suppress-${jobIdx}`}
            >
              <EyeOff className="w-3 h-3 mr-1" />
              Hide from customer
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
